import { DropdownArrow } from '#PRIVATE_SelectCommon';
import { IconButton } from '#UNSAFE_IconButton';
import { CancelS } from '#UNSAFE_RedwoodIcons/CancelS';
import { NavLeft } from '#UNSAFE_RedwoodIcons/NavLeft';
import { TextField } from '#UNSAFE_TextField';
import { useFormFieldContext } from '#hooks/UNSAFE_useFormFieldContext';
import { useTranslationBundle } from '#hooks/UNSAFE_useTranslationBundle';
import { BundleType } from '#resources/nls/bundle';
import { ComponentProps } from 'preact';

type PickedTextFieldProps = Pick<
  ComponentProps<typeof TextField>,
  | 'columnSpan'
  | 'compactUserAssistance'
  | 'contentVariant'
  | 'endContent'
  | 'hasZeroStartMargin'
  | 'id'
  | 'inlineUserAssistance'
  | 'label'
  | 'labelEdge'
  | 'labelStartWidth'
  | 'mainFieldRef'
  | 'onBlur'
  | 'onFocus'
  | 'onKeyDown'
  | 'onMouseDown'
  | 'onMouseEnter'
  | 'onMouseLeave'
  | 'resize'
  | 'rootRef'
  | 'startContent'
  | 'statusVariant'
  | 'styleVariant'
  | 'testId'
> & {
  children: ComponentProps<typeof TextField>['mainContent'];
};

type Props = PickedTextFieldProps & {
  hasInsideLabel?: boolean;
  isBackButtonShown?: boolean;
  isClearButtonShown?: boolean;
  isDropdownArrowShown?: boolean;
  onBackButtonClick?: ComponentProps<typeof IconButton>['onAction'];
  onClearButtonClick?: ComponentProps<typeof IconButton>['onAction'];
  onDropdownArrowClick?: ComponentProps<typeof DropdownArrow>['onClick'];
};

export function SelectSingleField({
  children,
  hasInsideLabel,
  isBackButtonShown,
  isClearButtonShown,
  isDropdownArrowShown,
  onBackButtonClick,
  onClearButtonClick,
  onDropdownArrowClick,
  ...passThroughTextFieldProps
}: Props) {
  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');
  const { isDisabled, isLoading } = useFormFieldContext();

  const startContent = isBackButtonShown ? (
    <IconButton
      aria-label={translations.selectMultiple_back()}
      size="xs"
      variant="ghost"
      onAction={onBackButtonClick}>
      <NavLeft />
    </IconButton>
  ) : null;

  const endContent = isLoading ? undefined : (
    <>
      {isClearButtonShown && (
        <IconButton
          aria-label={translations.formControl_clear()}
          size="sm"
          variant="ghost"
          onAction={onClearButtonClick}>
          <CancelS />
        </IconButton>
      )}
      {isDropdownArrowShown && (
        <DropdownArrow
          isDisabled={isDisabled}
          onClick={onDropdownArrowClick}
          testId={`${passThroughTextFieldProps.testId}_dropdownarrow`}
        />
      )}
    </>
  );

  return (
    <TextField
      startContent={startContent}
      mainContent={children}
      endContent={endContent}
      {...passThroughTextFieldProps}
    />
  );
}
